import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { API_URL } from "../../api/api";

import { toast } from "../../utils/toast";
const auth = () => {
  const t = localStorage.getItem("token");
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

const handle401 = (err) => {
  if (err?.response?.status === 401) {
    localStorage.clear();
    window.location.assign("/login");
    return true;
  }
  return false;
};

const fmtDateTime = (s) => {
  if (!s) return "—";
  const d = new Date(String(s).replace(" ", "T"));
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const STATUS_PILL = {
  pending:  { bg: "#fef9c3", color: "#854d0e", label: "Pending" },
  approved: { bg: "#dcfce7", color: "#166534", label: "Approved" },
  rejected: { bg: "#fee2e2", color: "#991b1b", label: "Rejected" },
};

const StatusPill = ({ status }) => {
  const s = STATUS_PILL[status] || STATUS_PILL.pending;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {s.label}
    </span>
  );
};

const RegularisationRequests = ({ canDecide = false, onSubmitNew, refreshKey = 0 }) => {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/attendance/regularise?status=all`,
        auth()
      );
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (handle401(err)) return;
      setError(err?.response?.data?.error || "Could not load requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  // `refreshKey` is bumped by the parent every time a request is submitted.
  // Re-running the fetch is the whole point of it being in this dep list —
  // without it the list only ever loaded on mount, so a request submitted from
  // the "+ New Request" button stayed invisible until a full page reload.
  useEffect(() => { load(); }, [load, refreshKey]);

  const decide = async (id, action) => {
    setBusyId(id);
    try {
      await axios.post(
        `${API_URL}/api/attendance/regularise/${id}/${action}`,
        {},
        auth()
      );
      load();
    } catch (err) {
      if (handle401(err)) return;
      toast.error(err?.response?.data?.error || `${action} failed.`);
    } finally {
      setBusyId(null);
    }
  };

  const filtered = rows.filter((r) =>
    tab === "pending" ? r.status === "pending" : r.status !== "pending"
  );
  const pendingCount = rows.filter((r) => r.status === "pending").length;

  // Whether this caller is looking at anyone else's requests, which is what
  // the Employee and Action columns are for. `canDecide` covers admin/HR and
  // anyone holding `manage_attendance`; `can_decide` on a row covers the
  // manager a request named, who has no company-wide authority but must still
  // see whose correction they are approving. Per row, because a reviewer's
  // list also contains their OWN requests — those they cannot decide, and the
  // server says so rather than the UI guessing from a role.
  const isReviewing = canDecide || rows.some((r) => r.can_decide);

  return (
    <div className="regularisation-panel">
      {/* Sub-tabs */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <ul className="leave-tabs" style={{ margin: 0 }}>
          <li
            className={tab === "pending" ? "active" : ""}
            onClick={() => setTab("pending")}
          >
            Pending ({pendingCount})
          </li>
          <li
            className={tab === "history" ? "active" : ""}
            onClick={() => setTab("history")}
          >
            History
          </li>
        </ul>

        {onSubmitNew && (
          <button className="org-gradi" onClick={onSubmitNew} style={{ fontSize: 13 }}>
            + New Request
          </button>
        )}
      </div>

      {error && (
        <div
          style={{
            marginBottom: 12,
            padding: "8px 12px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 8,
            color: "#b91c1c",
            fontSize: 13,
          }}
        >
          ⚠ {error}
        </div>
      )}

      {loading ? (
        <p style={{ padding: 16, color: "#64748b" }}>Loading…</p>
      ) : filtered.length === 0 ? (
        <p style={{ padding: 16, color: "#64748b", textAlign: "center" }}>
          {tab === "pending"
            ? "No pending regularisation requests."
            : "No request history yet."}
        </p>
      ) : (
        <div className="resp-table">
          <table className="data-table">
            <thead>
              <tr>
                {isReviewing && <th>Employee</th>}
                <th>Date</th>
                <th>Requested In</th>
                <th>Requested Out</th>
                <th>Reason</th>
                <th>Submitted</th>
                <th>Status</th>
                {tab === "history" && <th>Reviewed by</th>}
                {tab === "history" && <th>Reviewed at</th>}
                {tab === "pending" && isReviewing && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  {isReviewing && (
                    <td>
                      <b>{r.employee_name || `User #${r.user_id}`}</b>
                    </td>
                  )}
                  <td>{r.date}</td>
                  <td>{String(r.requested_in || "").slice(0, 5)}</td>
                  <td>{String(r.requested_out || "").slice(0, 5)}</td>
                  <td style={{ maxWidth: 200, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {r.reason}
                  </td>
                  <td>{fmtDateTime(r.created_at)}</td>
                  <td>
                    <StatusPill status={r.status} />
                  </td>
                  {tab === "history" && (
                    <td>{r.reviewed_by_name || "—"}</td>
                  )}
                  {tab === "history" && (
                    <td>{fmtDateTime(r.reviewed_at)}</td>
                  )}
                  {tab === "pending" && isReviewing && (
                    <td>
                      {r.can_decide ? (
                        <div className="row-actions">
                          <button
                            className="btn-approve"
                            disabled={busyId === r.id}
                            onClick={() => decide(r.id, "approve")}
                          >
                            {busyId === r.id ? "…" : "Approve"}
                          </button>
                          <button
                            className="btn-reject"
                            disabled={busyId === r.id}
                            onClick={() => decide(r.id, "reject")}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        // Their own request. Buttons here would 403 on click —
                        // nobody decides their own regularisation.
                        <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

RegularisationRequests.propTypes = {
  canDecide: PropTypes.bool,
  onSubmitNew: PropTypes.func,
  refreshKey: PropTypes.number,
};

export default RegularisationRequests;
